import { useEffect, useRef, useCallback, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useThreadStore, type ThreadListItem } from '@/stores/thread.store';
import { type ChatMessage } from '@/stores/chat.store';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';

// Stable empty arrays — prevents Zustand `?? []` selectors from creating new refs on every
// store change, which triggers useSyncExternalStore infinite re-render loops (React Error #185).
const EMPTY_THREADS: ThreadListItem[] = [];
const EMPTY_MESSAGES: ChatMessage[] = [];

/** Max time gap (ms) between consecutive messages from the same author to be grouped. */
const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

function shouldGroupMessage(current: ChatMessage, previous: ChatMessage | undefined): boolean {
  if (!previous) return false;
  if (current.authorId !== previous.authorId) return false;
  if (current.messageType === 'announcement' || current.requiresAcknowledgment) return false;
  if (previous.messageType === 'announcement' || previous.requiresAcknowledgment) return false;
  if (current.isDeleted || previous.isDeleted) return false;
  const currentTime = new Date(current.createdAt).getTime();
  const previousTime = new Date(previous.createdAt).getTime();
  return Math.abs(currentTime - previousTime) < MESSAGE_GROUP_WINDOW_MS;
}
import { useWebSocket } from '@/hooks/useWebSocket';
import {
  fetchThreads,
  fetchThreadMessages,
  sendThreadMessage,
  updateThread,
  editMessage,
  deleteMessage,
  markThreadRead,
  addReaction,
  removeReaction,
  acknowledgeMessage,
  createReplyLater,
  updateReplyLater,
  listReplyLater,
  bookmarkMessage,
  removeBookmark,
  toggleLinkPreview,
  pinMessage,
  unpinMessage,
  fetchPins,
} from '@/lib/api';
import { ReceiptDetailModal } from '@/components/admin/receipt-detail-modal';
import { MessageItem } from '@/components/chat/message-item';
import { MessageInput } from '@/components/chat/message-input';
import { TypingIndicator } from '@/components/chat/typing-indicator';
import { nanoid } from 'nanoid';
import type { WSServerEvent } from '@goodchat/shared/types';

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

// =============================================================
// Thread List Panel
// =============================================================

function ThreadListPanel({
  orgId,
  areaId,
  areaName,
  onSelectThread,
  onClose,
}: {
  orgId: string;
  areaId: string;
  areaName: string;
  onSelectThread: (threadId: string) => void;
  onClose: () => void;
}) {
  const params = useParams({ strict: false }) as { orgSlug?: string; areaSlug?: string };
  const threads = useThreadStore((s) => s.threadsByArea[areaId] ?? EMPTY_THREADS);
  const { setThreads, addThread, updateThread: updateThreadInStore } = useThreadStore();
  const [statusFilter, setStatusFilter] = useState<'open' | 'resolved' | 'all'>('open');

  useEffect(() => {
    if (!orgId || !areaId) return;
    fetchThreads(orgId, areaId, statusFilter)
      .then((data) => setThreads(areaId, data.threads))
      .catch(() => toast.error('Failed to load threads'));
  }, [orgId, areaId, statusFilter, setThreads]);

  const wsHandler = useCallback(
    (event: WSServerEvent) => {
      if (event.event === 'thread.new' && event.areaId === areaId) {
        addThread(areaId, event.thread);
      } else if (event.event === 'thread.updated') {
        updateThreadInStore(event.threadId, {
          title: event.title,
          isResolved: event.isResolved,
          updatedAt: event.updatedAt,
        });
      }
    },
    [areaId, addThread, updateThreadInStore],
  );
  useWebSocket(wsHandler);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-10 flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Threads in #{areaName}
        </span>
        <div className="flex items-center gap-1">
          <Link
            to="/org/$orgSlug/$areaSlug/threads/new"
            params={{ orgSlug: params.orgSlug ?? '', areaSlug: params.areaSlug ?? '' }}
            className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-gray-800"
          >
            New
          </Link>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            aria-label="Close thread panel"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-1 border-b border-gray-200 px-3 py-1.5 dark:border-gray-700">
        <button
          onClick={() => setStatusFilter('open')}
          className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
            statusFilter === 'open'
              ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
          }`}
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="9" />
          </svg>
          Open
        </button>
        <button
          onClick={() => setStatusFilter('resolved')}
          className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
            statusFilter === 'resolved'
              ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
          }`}
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Resolved
        </button>
        <button
          onClick={() => setStatusFilter('all')}
          className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
            statusFilter === 'all'
              ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
          }`}
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          All
        </button>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="flex h-32 items-center justify-center">
            <div className="text-center">
              <svg className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 005.586 13H4" />
              </svg>
              <p className="mt-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                No {statusFilter === 'all' ? '' : statusFilter} threads
              </p>
              <Link
                to="/org/$orgSlug/$areaSlug/threads/new"
                params={{ orgSlug: params.orgSlug ?? '', areaSlug: params.areaSlug ?? '' }}
                className="mt-1 inline-block text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Start a conversation thread
              </Link>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {threads.map((thread) => (
              <button
                key={thread.id}
                onClick={() => onSelectThread(thread.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {thread.author.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`truncate text-xs font-medium ${thread.unreadCount > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                      {thread.title}
                    </span>
                    {thread.isResolved && (
                      <span className="flex-shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                        Done
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-600 dark:text-gray-300">
                    {thread.messageCount} msgs
                    {thread.lastMessageAt ? ` · ${formatRelativeTime(thread.lastMessageAt)}` : ''}
                  </div>
                </div>
                {thread.unreadCount > 0 && (
                  <span className="min-w-[18px] rounded-full bg-blue-500 px-1 py-0.5 text-center text-[9px] font-bold text-white">
                    {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

// =============================================================
// Thread View Panel
// =============================================================

export function ThreadViewPanel({
  orgId,
  areaId,
  threadId,
  onBack,
  onClose,
}: {
  orgId: string;
  areaId: string;
  threadId: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const params = useParams({ strict: false }) as { orgSlug?: string; areaSlug?: string };
  const currentUser = useAuthStore((s) => s.user);
  const viewStyle = useAuthStore((s) => s.user?.messageViewStyle ?? 'classic');
  const orgs = useAuthStore((s) => s.orgs);
  const currentOrg = orgs.find((o) => o.id === orgId);
  const isAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const isManagerOrAbove = isAdmin || currentOrg?.role === 'manager';

  const threadsByArea = useThreadStore((s) => s.threadsByArea[areaId] ?? EMPTY_THREADS);
  const threadInfo = threadsByArea.find((t) => t.id === threadId);

  const messages = useThreadStore((s) => s.messagesByThread[threadId] ?? EMPTY_MESSAGES);
  const hasMore = useThreadStore((s) => s.hasMoreByThread[threadId] ?? true);
  const {
    setThreadMessages,
    prependThreadMessages,
    addThreadMessage,
    addPendingThreadMessage,
    replacePendingMessage,
    markPendingFailed,
    updateMessageContent,
    markMessageDeleted,
    updateThread: updateThreadInStore,
    addReactionToMessage,
    removeReactionFromMessage,
  } = useThreadStore();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // WS handler
  const wsHandler = useCallback(
    (event: WSServerEvent) => {
      if (!threadId) return;
      switch (event.event) {
        case 'message.new':
          if (event.threadId === threadId) {
            addThreadMessage(threadId, event.message as unknown as ChatMessage);
            markThreadRead(threadId).then(() => {
              useThreadStore.getState().markThreadAsRead(threadId);
            }).catch(() => toast.error('Failed to mark thread as read'));
          }
          break;
        case 'message.edited':
          updateMessageContent(event.messageId, threadId, event.content, event.editedAt);
          break;
        case 'message.deleted':
          markMessageDeleted(event.messageId, threadId);
          break;
        case 'thread.updated':
          if (event.threadId === threadId) {
            updateThreadInStore(threadId, {
              title: event.title,
              isResolved: event.isResolved,
              updatedAt: event.updatedAt,
            });
          }
          break;
        case 'reaction.added':
          addReactionToMessage(event.messageId, threadId, event.emoji, event.user, currentUser?.id ?? '');
          break;
        case 'reaction.removed':
          removeReactionFromMessage(event.messageId, threadId, event.emoji, event.userId, currentUser?.id ?? '');
          break;
      }
    },
    [threadId, addThreadMessage, updateMessageContent, markMessageDeleted, updateThreadInStore, addReactionToMessage, removeReactionFromMessage, currentUser?.id],
  );
  useWebSocket(wsHandler);

  // Load messages
  useEffect(() => {
    if (!threadId) return;
    initialLoadDoneRef.current = false;
    fetchThreadMessages(threadId)
      .then((result) => {
        setThreadMessages(threadId, result.data.reverse(), result.hasMore);
        initialLoadDoneRef.current = true;
      })
      .catch(() => toast.error('Failed to load thread messages'));
    markThreadRead(threadId).then(() => {
      useThreadStore.getState().markThreadAsRead(threadId);
    }).catch(() => toast.error('Failed to mark thread as read'));
  }, [threadId, setThreadMessages]);

  // Scroll on load more
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || isLoadingRef.current || !hasMore || !threadId) return;
    if (container.scrollTop < 100) {
      const oldest = messages[0];
      if (!oldest) return;
      isLoadingRef.current = true;
      const prevHeight = container.scrollHeight;
      fetchThreadMessages(threadId, { before: oldest.id })
        .then((result) => {
          prependThreadMessages(threadId, result.data.reverse(), result.hasMore);
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - prevHeight;
          });
        })
        .catch(() => toast.error('Failed to load older messages'))
        .finally(() => { isLoadingRef.current = false; });
    }
  }, [threadId, messages, hasMore, prependThreadMessages]);

  // Send
  const handleSend = useCallback(
    (content: string, replyToMessageId?: string, _attachmentIds?: string[], voiceMemoId?: string) => {
      if (!threadId || !currentUser) return;
      const tempId = nanoid();
      const pending: ChatMessage = {
        id: tempId,
        areaId,
        threadId,
        authorId: currentUser.id,
        content: content || null,
        messageType: voiceMemoId ? 'voice' : 'text',
        replyToMessageId: replyToMessageId ?? null,
        sequenceNumber: Date.now(),
        isDeleted: false,
        editedAt: null,
        createdAt: new Date().toISOString(),
        author: {
          id: currentUser.id,
          fullName: currentUser.fullName,
          displayName: currentUser.displayName,
          profilePictureUrl: currentUser.profilePictureUrl,
        },
        replyTo: null,
        tempId,
        isPending: true,
      };
      addPendingThreadMessage(threadId, pending);
      sendThreadMessage(threadId, {
        content: content || '',
        replyToMessageId,
        voiceMemoId,
        messageType: voiceMemoId ? 'voice' : undefined,
      })
        .then((result) => replacePendingMessage(tempId, result.message as unknown as ChatMessage))
        .catch(() => markPendingFailed(tempId));
    },
    [threadId, areaId, currentUser, addPendingThreadMessage, replacePendingMessage, markPendingFailed],
  );

  const handleEdit = useCallback((messageId: string, content: string) => {
    editMessage(messageId, content).catch(() => toast.error('Failed to edit message'));
  }, []);
  const handleDelete = useCallback((messageId: string) => {
    deleteMessage(messageId).catch(() => toast.error('Failed to delete message'));
  }, []);
  const handleReply = useCallback(() => {}, []);
  const handleRetry = useCallback(
    (message: ChatMessage) => {
      if (!threadId) return;
      handleSend(message.content!, message.replyToMessageId ?? undefined);
    },
    [threadId, handleSend],
  );
  const handleReact = useCallback(
    (messageId: string, emoji: string) => {
      if (!threadId || !currentUser) return;
      const msg = messages.find((m) => m.id === messageId);
      const existing = msg?.reactions?.find((r) => r.emoji === emoji);
      if (existing?.includesMe) {
        removeReactionFromMessage(messageId, threadId, emoji, currentUser.id, currentUser.id);
        removeReaction(messageId, emoji).catch(() => toast.error('Failed to remove reaction'));
      } else {
        addReactionToMessage(messageId, threadId, emoji, { userId: currentUser.id, displayName: currentUser.displayName }, currentUser.id);
        addReaction(messageId, emoji).catch(() => toast.error('Failed to add reaction'));
      }
    },
    [threadId, currentUser, messages, addReactionToMessage, removeReactionFromMessage],
  );
  const handleAcknowledge = useCallback((messageId: string) => {
    acknowledgeMessage(messageId).catch(() => toast.error('Failed to acknowledge message'));
  }, []);

  // Reply later
  const [replyLaterIds, setReplyLaterIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    listReplyLater({ status: 'active', limit: 50 })
      .then((res) => {
        const ids = new Set<string>();
        for (const item of res.items) {
          if (item.messageId) ids.add(item.messageId as string);
        }
        setReplyLaterIds(ids);
      })
      .catch(() => toast.error('Failed to load reply later items'));
  }, [threadId]);
  const handleFlagReplyLater = useCallback((messageId: string) => {
    createReplyLater(messageId)
      .then(() => setReplyLaterIds((prev) => new Set(prev).add(messageId)))
      .catch(() => toast.error('Failed to flag for reply later'));
  }, []);

  const handleReplyLaterWithSnooze = useCallback((messageId: string, snoozedUntil: string) => {
    createReplyLater(messageId)
      .then((res) => {
        setReplyLaterIds((prev) => new Set(prev).add(messageId));
        if (res.replyLater?.id) {
          updateReplyLater(res.replyLater.id as string, { snoozedUntil }).catch(() => toast.error('Failed to snooze'));
        }
      })
      .catch(() => toast.error('Failed to flag for reply later'));
  }, []);

  // Bookmark
  const [bookmarkIdMap, setBookmarkIdMap] = useState<Record<string, string>>({});
  const handleBookmark = useCallback(
    (messageId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      if (msg?.isBookmarked) {
        const bmId = bookmarkIdMap[messageId];
        if (bmId) {
          removeBookmark(bmId).then(() => {
            useThreadStore.getState().toggleBookmarked(messageId, threadId, false);
          }).catch(() => toast.error('Failed to remove bookmark'));
          setBookmarkIdMap((prev) => { const n = { ...prev }; delete n[messageId]; return n; });
        }
      } else {
        bookmarkMessage(messageId).then((res) => {
          useThreadStore.getState().toggleBookmarked(messageId, threadId, true);
          setBookmarkIdMap((prev) => ({ ...prev, [messageId]: res.bookmark.id }));
        }).catch(() => toast.error('Failed to bookmark message'));
      }
    },
    [threadId, messages, bookmarkIdMap],
  );
  const handleHideLinkPreview = useCallback((messageId: string) => {
    toggleLinkPreview(messageId, true).catch(() => toast.error('Failed to hide link preview'));
  }, []);

  // Pin handler — toggle pin/unpin (mirrors thread-view.tsx pattern)
  const pinIdMapRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!orgId || !areaId) return;
    fetchPins(orgId, areaId).then((res) => {
      const map: Record<string, string> = {};
      for (const p of res.pins) map[p.message.id] = p.id;
      pinIdMapRef.current = map;
    }).catch(() => toast.error('Failed to load pins'));
  }, [orgId, areaId]);

  const handlePin = useCallback(
    (messageId: string) => {
      if (!orgId || !areaId) return;
      const existingPinId = pinIdMapRef.current[messageId];
      if (existingPinId) {
        // Optimistic unpin
        useThreadStore.getState().togglePinned(messageId, threadId, false);
        delete pinIdMapRef.current[messageId];
        unpinMessage(orgId, areaId, existingPinId).catch(() => {
          // Revert on failure
          useThreadStore.getState().togglePinned(messageId, threadId, true);
          pinIdMapRef.current[messageId] = existingPinId;
          toast.error('Failed to unpin message');
        });
      } else {
        // Optimistic pin
        useThreadStore.getState().togglePinned(messageId, threadId, true);
        pinMessage(orgId, areaId, messageId).then((res) => {
          pinIdMapRef.current[messageId] = res.pin.id;
        }).catch(() => {
          // Revert on failure
          useThreadStore.getState().togglePinned(messageId, threadId, false);
          toast.error('Failed to pin message');
        });
      }
    },
    [orgId, areaId, threadId],
  );

  // Receipt detail modal state
  const [receiptMessageId, setReceiptMessageId] = useState<string | null>(null);
  const handleViewReceipts = useCallback(
    (messageId: string) => {
      setReceiptMessageId(messageId);
    },
    [],
  );

  // Resolve
  const handleToggleResolved = useCallback(() => {
    if (!threadId || !threadInfo) return;
    updateThread(threadId, { isResolved: !threadInfo.isResolved }).catch(() => toast.error('Failed to update thread'));
  }, [threadId, threadInfo]);

  const title = threadInfo?.title ?? 'Thread';
  const isResolved = threadInfo?.isResolved ?? false;

  const bottomRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  useEffect(() => {
    if (initialLoadDoneRef.current && messages.length > prevCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevCountRef.current = messages.length;
  }, [messages.length]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-10 flex-shrink-0 items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-700">
        <button
          onClick={onBack}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          aria-label="Back to thread list"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </span>
          {isResolved && (
            <span className="flex-shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
              Resolved
            </span>
          )}
        </div>
        <button
          onClick={handleToggleResolved}
          className={`flex-shrink-0 rounded px-2 py-1 text-xs font-medium transition-colors ${
            isResolved
              ? 'border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {isResolved ? 'Reopen' : 'Resolve'}
        </button>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          aria-label="Close thread panel"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        {hasMore && (
          <div className="py-2 text-center text-[10px] text-gray-400">
            Scroll up to load more...
          </div>
        )}

        {messages.length === 0 && (
          <div className="flex h-24 items-center justify-center">
            <p className="text-xs text-gray-600 dark:text-gray-300">
              No messages in this thread yet.
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={msg.tempId ?? msg.id}>
            <MessageItem
              message={msg}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onReply={handleReply}
              onRetry={handleRetry}
              onReact={handleReact}
              onAcknowledge={handleAcknowledge}
              onViewReceipts={handleViewReceipts}
              onFlagReplyLater={handleFlagReplyLater}
              onReplyLaterWithSnooze={handleReplyLaterWithSnooze}
              onPin={handlePin}
              onBookmark={handleBookmark}
              onHideLinkPreview={handleHideLinkPreview}
              canPin={isManagerOrAbove}
              isAdmin={isManagerOrAbove}
              isReplyLater={replyLaterIds.has(msg.id)}
              isGrouped={shouldGroupMessage(msg, messages[idx - 1])}
              viewStyle={viewStyle}
              orgSlug={params.orgSlug}
              areaSlug={params.areaSlug}
            />
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Typing indicator */}
      <TypingIndicator areaId={areaId} threadId={threadId} />

      {/* Input */}
      <MessageInput areaId={areaId} orgId={orgId} threadId={threadId} onSend={handleSend} />

      {/* Receipt detail modal (admin) */}
      <ReceiptDetailModal
        messageId={receiptMessageId ?? ''}
        open={!!receiptMessageId}
        onClose={() => setReceiptMessageId(null)}
      />
    </div>
  );
}

// =============================================================
// Main ThreadSidePanel component
// =============================================================

export function ThreadSidePanel({
  orgId,
  areaId,
  areaName,
  threadId,
  onSelectThread,
  onClose,
}: {
  orgId: string;
  areaId: string;
  areaName: string;
  threadId: string | null; // null = show list, string = show that thread
  onSelectThread: (threadId: string | null) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (threadId) {
          onSelectThread(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [threadId, onSelectThread, onClose]);

  return (
    <>
      {/* Backdrop on mobile */}
      <div className="fixed inset-0 z-40 bg-black/40 sm:hidden" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white sm:relative sm:inset-auto sm:z-auto sm:w-96 sm:flex-shrink-0 sm:border-l sm:border-gray-200 dark:bg-gray-900 sm:dark:border-gray-700">
        {threadId ? (
          <ThreadViewPanel
            orgId={orgId}
            areaId={areaId}
            threadId={threadId}
            onBack={() => onSelectThread(null)}
            onClose={onClose}
          />
        ) : (
          <ThreadListPanel
            orgId={orgId}
            areaId={areaId}
            areaName={areaName}
            onSelectThread={(id) => onSelectThread(id)}
            onClose={onClose}
          />
        )}
      </div>
    </>
  );
}
